if (!customElements.get('o11-basket')) {
  // London calendar days avoid shopper time-zone and daylight-saving offsets.
  function estimatedDeliveryDate(now, transitDays, cutoffHour, excludedDates, validUntil) {
    if (!Number.isInteger(transitDays) || transitDays < 1 || transitDays > 10) return null;
    if (!Number.isInteger(cutoffHour) || cutoffHour < 1 || cutoffHour > 23) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) return null;
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/London',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',hourCycle:'h23'}).formatToParts(now);
    const part = name => Number(parts.find(value => value.type === name).value);
    const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day'), 12));
    const holidays = new Set(excludedDates.split(/[\s,]+/).filter(Boolean));
    const isWorkingDay = day => day.getUTCDay() !== 0 && day.getUTCDay() !== 6 && !holidays.has(day.toISOString().slice(0,10));
    if (date.toISOString().slice(0,10) > validUntil) return null;
    const sameDayDispatch = isWorkingDay(date) && part('hour') < cutoffHour;
    if (!sameDayDispatch) {
      do {
        date.setUTCDate(date.getUTCDate() + 1);
        if (date.toISOString().slice(0,10) > validUntil) return null;
      } while (!isWorkingDay(date));
    }
    const dispatchDate = new Date(date);
    let days = 0;
    while (days < transitDays) {
      date.setUTCDate(date.getUTCDate() + 1);
      const iso = date.toISOString().slice(0,10);
      if (iso > validUntil) return null;
      if (isWorkingDay(date)) days++;
    }
    return {date,dispatchDate,sameDayDispatch};
  }
  class O11Basket extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.busy = false;
      this.updateDeliveryEstimate();
      this.dateRefresh = window.setInterval(() => this.updateDeliveryEstimate(), 60000);
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-quantity-step]');
        const remove = event.target.closest('[data-remove]');
        if (step) {
          event.preventDefault();
          if (this.busy) return;
          const input = step.closest('.o11-quantity').querySelector('input');
          const quantity = Math.max(0, Number(input.value) + Number(step.dataset.quantityStep));
          this.change(input.dataset.key, quantity, input.id);
        } else if (remove) {
          event.preventDefault();
          if (!this.busy) this.change(remove.dataset.remove, 0);
        }
      });
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.matches('[data-quantity]')) {
          if (this.busy || !input.checkValidity()) { input.value = input.dataset.quantity; return; }
          this.change(input.dataset.key, Number(input.value), input.id);
        }
        if (input.matches('.o11-offer-form select')) {
          const price = input.selectedOptions[0]?.dataset.price;
          if (price) input.closest('.o11-basket-offer').querySelector('[data-offer-price]').textContent = price;
        }
      });
      this.addEventListener('submit', (event) => {
        const form = event.target;
        if (this.busy) { event.preventDefault(); return; }
        if (form.matches('.o11-offer-form')) {
          event.preventDefault();
          event.stopPropagation();
          if (form.reportValidity()) this.addOffer(form);
        } else if (form.matches('[data-cart-form]') && event.submitter?.name !== 'checkout') {
          event.preventDefault();
          if (form.reportValidity()) {
            const updates = {};
            form.querySelectorAll('[data-quantity]').forEach(input => { updates[input.dataset.key] = Number(input.value); });
            this.mutate(this.dataset.updateUrl, {updates, note:form.querySelector('[name=note]')?.value}, this.dataset.updated);
          }
        }
      });
    }
    disconnectedCallback() {
      window.clearInterval(this.dateRefresh);
    }
    updateDeliveryEstimate() {
      const estimate = this.querySelector('[data-delivery-estimate]');
      if (!estimate) return;
      const cutoff = Number(estimate.dataset.cutoffHour);
      const result = estimatedDeliveryDate(new Date(), Number(estimate.dataset.transitDays), cutoff, estimate.dataset.excludedDates || '', estimate.dataset.validUntil || '');
      const dated = estimate.querySelector('[data-delivery-dated]');
      const fallback = estimate.querySelector('[data-delivery-fallback]');
      const label = estimate.querySelector('[data-delivery-label]');
      const dispatch = estimate.querySelector('[data-delivery-dispatch]');
      dated.hidden = !result;
      fallback.hidden = Boolean(result);
      dispatch.hidden = !result;
      label.textContent = estimate.dataset.orderToday;
      if (!result) return;
      const {date,dispatchDate,sameDayDispatch} = result;
      const formatter = new Intl.DateTimeFormat('en-GB', {timeZone:'UTC',weekday:'long',day:'numeric',month:'long'});
      const cutoffText = `${cutoff % 12 || 12}${cutoff < 12 ? 'am' : 'pm'}`;
      if (sameDayDispatch) label.textContent = estimate.dataset.orderBefore.replace('[time]',cutoffText);
      dispatch.textContent = sameDayDispatch ? estimate.dataset.dispatchToday : estimate.dataset.dispatchOn.replace('[date]',formatter.format(dispatchDate));
      const time = estimate.querySelector('[data-delivery-date]');
      time.dateTime = date.toISOString().slice(0,10);
      time.textContent = formatter.format(date);
    }
    change(key, quantity, focusId) {
      if (!Number.isInteger(quantity) || quantity < 0) return;
      this.mutate(this.dataset.changeUrl, {id:key, quantity}, this.dataset.updated, focusId);
    }
    async addOffer(form) {
      if (this.busy) return;
      const data = new FormData(form);
      const note = this.querySelector('[name=note]');
      const noteValue = note?.value;
      const shouldSaveNote = note && noteValue !== note.defaultValue;
      const status = this.querySelector('.o11-basket-status');
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      this.setBusy(true);
      try {
        if (shouldSaveNote) {
          const savedNote = await fetch(this.dataset.updateUrl, {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({note:noteValue})});
          if (!savedNote.ok) throw new Error(this.dataset.error);
        }
        const returnUrl = new URL(this.dataset.cartUrl, location.origin);
        const current = new URL(location.href);
        for (const name of ['view','preview_theme_id','pb']) {
          if (current.searchParams.has(name)) returnUrl.searchParams.set(name,current.searchParams.get(name));
        }
        data.set('return_to',returnUrl.pathname + returnUrl.search);
        // Submit through Shopify's native product form so cart-app Ajax hooks
        // do not open another drawer over this basket page.
        const submitForm = document.createElement('form');
        submitForm.method = 'POST';
        submitForm.action = form.action;
        submitForm.hidden = true;
        for (const [name,value] of data) {
          if (typeof value !== 'string') continue;
          const field = document.createElement('input');
          field.type = 'hidden'; field.name = name; field.value = value;
          submitForm.append(field);
        }
        document.body.append(submitForm);
        HTMLFormElement.prototype.submit.call(submitForm);
      } catch (error) {
        status.textContent = error.message || this.dataset.error;
        status.dataset.error = 'true';
        this.setBusy(false);
      }
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('button,input,select').forEach(control => {
        if (value) { control.dataset.wasDisabled = String(control.disabled); control.disabled = true; }
        else { control.disabled = control.dataset.wasDisabled === 'true'; delete control.dataset.wasDisabled; }
      });
    }
    async mutate(url, payload, message, focusId) {
      if (this.busy) return;
      const draftNote = this.querySelector('[name=note]')?.value;
      const status = this.querySelector('.o11-basket-status');
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      this.setBusy(true);
      let saved = false;
      try {
        const formData = payload instanceof FormData;
        const response = await fetch(url, {
          method:'POST', credentials:'same-origin',
          headers:formData ? {'Accept':'application/json'} : {'Accept':'application/json','Content-Type':'application/json'},
          body:formData ? payload : JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
        saved = true;
        const renderUrl = new URL(this.dataset.cartUrl, location.origin);
        const current = new URL(location.href);
        for (const name of ['view','preview_theme_id','pb']) {
          if (current.searchParams.has(name)) renderUrl.searchParams.set(name, current.searchParams.get(name));
        }
        renderUrl.searchParams.set('section_id', this.dataset.section);
        const rendered = await fetch(renderUrl, {credentials:'same-origin',cache:'no-store'});
        if (!rendered.ok) throw new Error('Cart refresh failed');
        const html = new DOMParser().parseFromString(await rendered.text(), 'text/html');
        const fresh = html.querySelector('o11-basket');
        if (!fresh) throw new Error('Cart refresh failed');
        this.replaceWith(fresh);
        if (draftNote !== undefined && fresh.querySelector('[name=note]')) fresh.querySelector('[name=note]').value = draftNote;
        fresh.querySelector('.o11-basket-status').textContent = message;
        const cartLink = document.querySelector('.o11-cart');
        if (cartLink) {
          let badge = cartLink.querySelector('span');
          if (Number(fresh.dataset.count) > 0) {
            if (!badge) { badge = document.createElement('span'); cartLink.append(badge); }
            badge.textContent = fresh.dataset.count;
          } else if (badge) badge.remove();
        }
        const focus = focusId ? document.getElementById(focusId) : fresh.querySelector('.o11-basket-status');
        if (focus) focus.focus({preventScroll:true});
      } catch (error) {
        if (saved) { location.reload(); return; }
        status.textContent = error.message || this.dataset.error;
        status.dataset.error = 'true';
        this.querySelectorAll('[data-quantity]').forEach(input => {input.value = input.dataset.quantity;});
        this.setBusy(false);
        status.focus({preventScroll:true});
      }
    }
  }
  customElements.define('o11-basket', O11Basket);
}
